import _, {
  cloneDeep,
  findIndex,
  get,
  groupBy,
  isEmpty,
  isNil,
  values,
} from 'lodash';
import { createSelector } from 'reselect';
import { DisplayClassification, RoleCode } from 'src/components/Exchange/types';
import config from 'src/config/config';
import {
  AGREEMENT_TYPES,
  BUY_TRANSACTION,
  dailyMaximums,
  DISPLAY_TYPE_SORT_ENUM_MAP,
  EXCHANGE_TRANSACTION,
  ProductTypes,
  PRODUCT_TYPE_SORT_ENUM_MAP,
  SELL_TRANSACTION,
  SystemCode,
} from 'src/settings/settings';
import { formatUnixDate, getMonthName } from 'src/utils/dates/date-utility';
import {
  validateBuyFund,
  validateRecurringBuyFund,
} from 'src/utils/form/validate/validate';
import { updateBuyFundNotesForFund } from 'src/utils/funds/buy/buy-fund-notes-utility';
import {
  checkFullyContributedIRA,
  getFundsByKeys,
  isIRA,
  setFundPendingTransaction,
  setMoneyMarketReformFundMessages,
} from 'src/utils/funds/funds-utility';
import {
  updateRecurringBuyFundNotesForFund,
  updatIRAAutoMaxFundNotesForFund,
} from 'src/utils/funds/recurringBuy/recurring-buy-fund-notes-utility';
import { updateAddFundNotesForFund } from 'src/utils/funds/shared/add-fund-notes-utility';
import { checkFoundFeaturesByCode } from 'src/utils/funds/shared/shared-fund-notes-utility';
import { appendBasePath } from 'src/utils/nav.utils';
import { bankAssociatedWithFund } from 'src/utils/shoppingCart/shopping-cart-utility';
import {
  generateRecurringPurchase,
  getDailyMaxLimit,
  getPaperless,
} from 'src/utils/submission/submission-utility';
import {
  isBuy,
  isCollegeContribute,
  isCollegeDistribute,
  isSell,
} from 'src/utils/transactions-util';
import { coalesce, getClientType, stringPropSorter } from 'src/utils/utils';
import { automaticEditActivityID } from '../automaticEdit/automaticEdit.selectors';
import {
  getAutoExchanges,
  getAutoMax,
  getHasAutoMaxTransactions,
  getRecurringPurchases,
  getRecurringRedemptions,
  getUserAutoMaxPOAs,
} from '../automaticInvestments/automaticInvestments.selectors';
import {
  getSelectedBank,
  getSelectedBankAgreementMap,
} from '../bankSelection/bankSelection.selectors';
import { getBucketRoll } from '../bucketRoll/bucketRoll.selectors';
import { getExistingBanks } from '../existingBanks/existingBanks.selectors';
import {
  getAllFundsByTicker,
  getAllFundsList,
} from '../funds/AllFunds/allFunds.selectors';
import { getIRALimitData } from '../funds/IRA/IRA.selectors';
import {
  getAgreementMap,
  getAllIRAFunds,
  getDisplayTradeDate,
  getUserFundsRaw,
} from '../funds/UserFunds/userFunds.selectors';
import { getMoneyMarketReformFundMessages } from '../moneyMarketEvent/moneyMarketEvent.selectors';
import {
  getBuyPendingTransactionsTotal,
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
} from '../pendingTransactions/pendingTransactions.selectors';
import { getPaperlessSettings } from '../user/Paperless/paperless.selectors';
import {
  getPersonalInformationData,
  getUserBPID,
} from '../user/PersonalInformation/personalInformation.selectors';
import { getSelectedRegistrationType } from '../user/UserRegistrationTypes/UserRegistrationTypes.selectors';
import { getCartLookup, getNewFunds } from './selectors';
import { getIRALimitSearchDataByPoid } from '../funds/IRALimitSearch/IRALimitSearch.selectors.js';
import { getUserBusinessPartyId } from '../user/PersonalInformation/personalInformation.selectors';
import { getSystematicActivityRMDs } from '../RMD/RMDSetting.selectors';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../authentication/authentication.module.selectors';
import { isAPP, isRAS } from '../../../utils/advice/advice-utility';

export const EMPTY_AGREEMENT_MAP = {
  associableLookup: {},
  associatedLookup: {},
  associatedPurchaseRestrictedLookup: {},
  restrictedLookup: {},
};
export * from './selectors';

/**
 * Return the raw user funds in a simple array. Differentiates from getUserFundsRaw since that returns
 * a complex structure. Differentiates from GetUserFundList since that combines info from all funds
 * and some calculations.
 *
 * @function
 * @param state
 * @returns {UserFund[]}
 */
export const getUserFundsRawList = createSelector(
  [getUserFundsRaw],
  getFundsByKeys
);

/**
 * Returns the IRA contribution year based on the server-provided trade date
 * @function
 * @param state
 * @returns {Number}
 */
export const getCurrentContributionYear = createSelector(
  getDisplayTradeDate,
  (displayTradeDate) =>
    displayTradeDate
      ? parseInt(formatUnixDate(displayTradeDate, 'YYYY'))
      : new Date().getFullYear()
);

/**
 * Returns the prior IRA contribution year based on the server-provided trade date
 * @function
 * @param state
 * @returns {Number}
 */
export const getPriorContributionYear = createSelector(
  getCurrentContributionYear,
  (currentContributionYear) => currentContributionYear - 1
);

/**
 * Simple selector to extract the quick buy agreement ID if provided and not yet cleared out by 'Show all'
 * @param state
 * @returns {number|null}
 */
export const getQuickBuyAgreementID = (state) =>
  state.quickBuy.quickBuyAgreements[0];

/**
 * Simple selector to extract the quick buy product type if provided and not yet cleared out by 'Show all'
 * @param state
 * @returns {string|null}
 */
export const getQuickBuyProductType = (state) => state.quickBuy.quickBuyType;

/**
 * Boolean selector to check whether it is corporate account or not.
 */
export const getIsCorporateAccountType = (state) =>
  state.personalInformation.data.type === 'ORGANIZATION';

export const getMinorIRA = (state) => state.IRALimit.minorData;

export const getRMData = (state) => state.userManager.manager;

export const banksMatch = (bank, bankAgreementKey) =>
  bank.bankAgreementID ===
  (!!bankAgreementKey ? bankAgreementKey.bankAgreementID : null);

/**
 * Filters all funds to exclude iClass since nobody can ever buy those online and those without a ticker
 * @param state
 * @returns {AllFund[]}
 */
export const getAddFundList = createSelector(
  getAllFundsList,
  getMoneyMarketReformFundMessages,
  getNewFunds,
  getSelectedRegistrationType,
  (
    allFunds,
    moneyMarketReformFundMessages,
    newFunds,
    selectedRegistrationType
  ) => {
    return allFunds
      .filter(
        (fund) => fund?.openToNewInvestors || fund?.openToSubsequentInvestments
      )
      .filter(
        (fund) =>
          fund.ticker !== null &&
          fund.ticker !== '' &&
          fund.ticker.toUpperCase() !== 'ICFXX' &&
          fund.merged !== true
      )
      .map((fund) => {
        setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
        return updateAddFundNotesForFund(
          /** @type {AllFund} */ { ...fund, notes: {} },
          newFunds,
          selectedRegistrationType
        );
      })
      .sort(stringPropSorter((fund) => fund.name));
  }
);

/**
 * Returns the maximum dollars the user can buy based on the selected bank and their client type.
 * @param state
 * @returns {number}
 */
export const getNonRetirementMaxLimit = createSelector(
  getPersonalInformationData,
  getSelectedBank,
  (personalData, selectedBank) =>
    getDailyMaxLimit(getClientType(personalData.entitySegment), selectedBank)
);

export function getIsNonRetirementLikeFund(userFund) {
  if (
    isNil(userFund) ||
    isNil(userFund.displayClassification) ||
    isNil(userFund.agreementType)
  ) {
    return false;
  }
  return (
    userFund.displayClassification === DisplayClassification.NON_RETIREMENT ||
    userFund.displayClassification === DisplayClassification.COLLEGE_SAVINGS ||
    userFund.agreementType === AGREEMENT_TYPES.NON_FIDUCIARY_PLAN ||
    userFund.agreementType === AGREEMENT_TYPES.COLLEGE_SAVINGS
  );
}
export function getIsMinorIRA(userFund) {
  const { agreementParties } = userFund;
  return agreementParties?.some(
    (agreement) => agreement.roleCode === RoleCode.MINOR
  );
}

export function getIsPoaIRA(userFund, userBPID) {
  const { agreementParties, agreementType } = userFund;

  return agreementParties && isIRA(userFund)
    ? agreementParties
        .map(
          (party) =>
            party.primaryParty === 'N' && party.businessPartyID === userBPID
        )
        .includes(true) && agreementType !== 'TRUST'
    : false;
}

export function getMinorIRAID(userFund, code) {
  const { agreementParties } = userFund;
  const agreementParty =
    agreementParties &&
    agreementParties.find((agreement) => agreement.roleCode === code);
  return get(agreementParty, 'businessPartyID');
}

export function getPoaIRAID(userFund) {
  const { agreementParties } = userFund;

  const poaParty = agreementParties.filter(
    (party) => party.primaryParty === 'Y'
  )[0];

  return poaParty.businessPartyID;
}

export function getIRAOwnerName(userFund, code) {
  const { agreementParties } = userFund;
  const agreementParty =
    agreementParties &&
    agreementParties.find((agreement) => agreement.roleCode === code);
  return get(agreementParty, 'fullName');
}

/**
 * Complex selector to extract the user funds (all funds currently associated with the user) and combine the
 * data from those funds with the data from the all funds array, plus manipulate sortRank so we can interweave
 * newly added funds.
 * @param state
 * @returns {MergedFund[]}
 */
export const getUserFundList = createSelector(
  getAllFundsByTicker,
  getUserFundsRaw,
  getUserBusinessPartyId,
  (allFundsByTicker, userFunds, userBPID) =>
    getFundsByKeys(userFunds).map((userFund) => {
      const allFund =
        allFundsByTicker[
          userFund.ticker || `${userFund.productType}${userFund.fundName}`
        ] || {};
      const isMinorIRA = getIsMinorIRA(userFund);
      const isPoaIRA = getIsPoaIRA(userFund, userBPID);
      const BPID = isMinorIRA
        ? getMinorIRAID(userFund, RoleCode.MINOR)
        : isPoaIRA
        ? getPoaIRAID(userFund)
        : 0;
      const ownerName = isMinorIRA
        ? getIRAOwnerName(userFund, RoleCode.MINOR)
        : getIRAOwnerName(userFund, 'CUST');

      return {
        ...allFund,
        ...userFund,
        BPID,
        ownerName,
        sharePrice: allFund?.price?.price,
        shareAsOf: allFund?.price?.asOfDate,
        sortRank: userFund.sortRank * 1000, // leave space for new funds in between existing
      };
    })
);

export const doesUserOwnAtleast1IRA = createSelector(
  getUserFundList,
  getUserBusinessPartyId,
  (userFunds, userBPID) => {
    const owner = userFunds.map((fund) => {
      return fund?.agreementParties?.filter(
        (roles) => roles?.roleCode === 'OWNR'
      );
    });

    return owner?.some((entity) => {
      return entity?.some(
        (businessParty) => businessParty.businessPartyID === userBPID
      );
    });
  }
);

/**
 * Simple selector to extract the fund from user funds list if the agreement ID is provided and not yet cleared out by 'Show all'
 * @param state
 * @returns {number|null}
 */
export const getFundBySelectedAgreementId = createSelector(
  [getUserFundList, getQuickBuyAgreementID],
  (userFundList, quickBuyAgreementID) => {
    return userFundList.filter(
      (fund) => fund.agreementID === quickBuyAgreementID
    );
  }
);

export const getIRADataLookUp = createSelector(
  getMinorIRA,
  getIRALimitData,
  getUserFundList,
  (minorIRA, IRALimitData, userFunds) => {
    const minorIRAData = cloneDeep(minorIRA);
    const groupedMinorData = groupBy(minorIRAData, function (IRA) {
      return IRA.minorBusinessPartyID;
    });
    const IRADataLookUp = Object.assign(groupedMinorData, {
      0: [IRALimitData],
    });
    userFunds.forEach((fund) => {
      Object.keys(IRADataLookUp).forEach((key) => {
        IRADataLookUp[key][0]['fullyContributedIRA'] = checkFullyContributedIRA(
          IRADataLookUp[key][0]
        );
        if (fund.BPID === parseInt(key)) {
          return (IRADataLookUp[key][0]['ownerName'] = fund.ownerName);
        }
      });
    });
    return IRADataLookUp;
  }
);

function getAllIRADataLookUp(prefix) {
  return createSelector(
    [
      getIRADataLookUp,
      getCartLookup,
      getUserFundList,
      getNewFunds,
      getIRALimitSearchDataByPoid,
    ],
    (IRADataLookUp, cartLookup, userFunds, newFunds, poaLimitData) => {
      const IRAData = cloneDeep(IRADataLookUp);
      const data = IRAData;
      const poaIraData = cloneDeep(poaLimitData);
      Object.keys(poaIraData).forEach((key) => {
        return (poaIraData[key] = [poaIraData[key]['data']]);
      });
      Object.assign(data, poaIraData);
      Object.keys(data).forEach((key) => {
        const limit = data[key][0][`${prefix}YearRemainingContributions`];
        return (data[key][0][`${prefix}YearInclusiveRemainingContributions`] =
          limit);
      });
      userFunds.concat(newFunds).forEach((fund) => {
        const cart = cartLookup[fund.agreementID];
        if (
          !getIsNonRetirementLikeFund(fund) &&
          cart &&
          cart.selected &&
          !isNil(cart.BPID)
        ) {
          const cartId = cart.poaPartyID ? cart.poaPartyID : cart.BPID;
          const amount = cart[`${prefix}YearAmount`];
          if (data[cartId]) {
            return (data[cartId][0][
              `${prefix}YearInclusiveRemainingContributions`
            ] -= coalesce(amount, 0));
          }
        }
      });
      return data;
    }
  );
}
/**
 * Complex selector to calculate the total purchase amount of all items in teh shopping cart. Does NOT filter out
 * funds with errors.
 * @param state
 * @returns {number}
 */

export const getCurrentShoppingCart = createSelector(
  getCartLookup,
  (cartLookup) => {
    return cartLookup;
  }
);

export const getCurrentShoppingCartAgreementIDs = createSelector(
  getCurrentShoppingCart,
  (cartLookup) => {
    return Object.keys(cartLookup).map((agreementIDs) => {
      return agreementIDs;
    });
  }
);

export const getShoppingCartTotal = createSelector(
  getCartLookup,
  (cartLookup) =>
    values(cartLookup).reduce(
      (total, cartItem) =>
        total +
        (cartItem.selected
          ? coalesce(cartItem.priorYearAmount, 0) +
            coalesce(cartItem.currentYearAmount, 0)
          : 0),
      0
    )
);

/**
 * Complex selector to calculate the total purchase amount of all IRA items in the shopping cart.
 * Does NOT filter out funds with errors.
 *
 * @param state
 * @returns {number}
 */
export const getShoppingCartTotalForSelectedIRA = createSelector(
  getCartLookup,
  (cartLookup) =>
    values(cartLookup).reduce(
      (total, cartItem) =>
        total +
        (cartItem.selected && cartItem.isIRA
          ? coalesce(cartItem.currentYearAmount, 0)
          : 0),
      0
    )
);

/**
 * Selector generator to calculate "inclusive" remaining IRA contributions which are the server-provided remaining
 * contributions minus IRA purchases currently in the shopping cart.
 * @param {string} prefix
 * @returns {function}
 */
const createInclusiveRemainingContributionSelector = (prefix) =>
  createSelector(
    getCartLookup,
    getIRALimitData,
    getNewFunds,
    getUserFundList,
    (cartLookup, iraLimitData, newFunds, userFunds) => {
      function inclusiveRemainingContributionsReducer(limit, fund) {
        const cart = cartLookup[fund.agreementID];
        if (
          cart === undefined ||
          !cart.selected ||
          getIsNonRetirementLikeFund(fund)
        ) {
          return limit;
        }
        return limit - coalesce(cart[`${prefix}YearAmount`], 0);
      }
      const original = coalesce(
        iraLimitData[`${prefix}YearRemainingContributions`],
        0
      );
      const minusExisting = userFunds.reduce(
        inclusiveRemainingContributionsReducer,
        original
      );
      return newFunds.reduce(
        inclusiveRemainingContributionsReducer,
        minusExisting
      );
    }
  );

/**
 * Complex selector to calculate "inclusive" current remaining IRA contributions--server-provided minus what's already
 * in the shopping cart.
 * @param state
 * @returns {number}
 */
export const getCurrentYearInclusiveRemainingContributions =
  createInclusiveRemainingContributionSelector('current');

/**
 * Complex selector to calculate "inclusive" prior remaining IRA contributions--server-provided minus what's already
 * in the shopping cart.
 * @param state
 * @returns {number}
 */
export const getPriorYearInclusiveRemainingContributions =
  createInclusiveRemainingContributionSelector('prior');
export const getCurrentYearAllIRADataLookUp = getAllIRADataLookUp('current');
export const getPriorYearAllIRADataLookUp = getAllIRADataLookUp('prior');

export const getHiddenAccountNumber = createSelector(
  isSalesforceRep,
  isCustomerWebViewEnabled,
  getNewFunds,
  getQuickBuyAgreementID,
  (state) => state.quickSell.quickSellAgreements[0],
  getUserFundList,
  (
    isSalesforceRep,
    customerWebViewEnabled,
    newFunds,
    quickBuyAgreementID,
    quickSellAgreementID,
    userFundList
  ) => {
    const quickAgreemntID =
      isBuy() || isCollegeContribute()
        ? quickBuyAgreementID
        : isSell() || isCollegeDistribute()
        ? quickSellAgreementID
        : null;
    return (
      quickAgreemntID
        ? userFundList.filter((fund) => fund.agreementID === quickAgreemntID)
        : newFunds.length
        ? userFundList.concat(newFunds).sort((x, y) => x.sortRank - y.sortRank)
        : userFundList
    ).filter(
      (fund) =>
        !(!customerWebViewEnabled && isSalesforceRep) &&
        fund.hiddenFromOnlineCustomer
    ).length;
  }
);
/**
 * Most complex selector to extract and calculate the "available funds" list. This is all funds the user owns,
 * plus all newly added funds, merged with data from both the user fund list and all funds list, with the selected
 * bank flag (bankAssociable & bankAssociated), with fund notes (notes object) and validated against fund rules
 * which adds currentYearError and priorYearError fields.
 * @param state
 * @returns {AvailableFund[]}
 */
export const getAvailableFundList = createSelector(
  [
    isSalesforceRep,
    isCustomerWebViewEnabled,
    getAgreementMap,
    getBucketRoll,
    getBuyPendingTransactionsTotal,
    getCartLookup,
    getIRALimitData,
    getMoneyMarketReformFundMessages,
    getNewFunds,
    getNonRetirementMaxLimit,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getPersonalInformationData,
    getQuickBuyAgreementID,
    getSelectedBank,
    getSelectedBankAgreementMap,
    getShoppingCartTotal,
    getUserFundList,
    getAutoMax,
    getAutoExchanges,
    getRecurringPurchases,
    getRecurringRedemptions,
    getCurrentYearAllIRADataLookUp,
    getPriorYearAllIRADataLookUp,
  ],
  (
    isSalesforceRep,
    customerWebViewEnabled,
    agreementMap,
    bucketRoll,
    buyPendingTransactionsTotal,
    cartLookup,
    IRALimitData,
    moneyMarketReformFundMessages,
    newFunds,
    nonRetirementMaxLimit,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    personalInformationData,
    quickBuyAgreementID,
    selectedBank,
    selectedBankAgreementMap,
    shoppingCartTotal,
    userFundList,
    autoMax,
    autoExchanges,
    recurringPurchases,
    recurringRedemptions,
    currentYearAllIRADataLookUp,
    priorYearAllIRADataLookUp
  ) =>
    (quickBuyAgreementID
      ? userFundList.filter((fund) => fund.agreementID === quickBuyAgreementID)
      : newFunds.length
      ? userFundList.concat(newFunds).sort((x, y) => x.sortRank - y.sortRank)
      : userFundList
    )
      .map((origFund) => {
        const nonRetirementLike = getIsNonRetirementLikeFund(origFund);
        const currentYearIRADataLookUp =
          currentYearAllIRADataLookUp[origFund.BPID] || {};
        const priorYearIRADataLookUp =
          priorYearAllIRADataLookUp[origFund.BPID] || {};
        const currentYearAllIRAData =
          currentYearIRADataLookUp && currentYearIRADataLookUp[0];
        const priorYearAllIRAData =
          priorYearIRADataLookUp && priorYearIRADataLookUp[0];
        const fund = {
          ...origFund,
          ...currentYearAllIRAData,
          ...priorYearAllIRAData,
          notes: {},
          disabled: false,
          hasRecurring:
            !!autoMax[origFund.agreementID] ||
            !!recurringPurchases[origFund.agreementID] ||
            !!recurringRedemptions[origFund.agreementID] ||
            !!autoExchanges[origFund.agreementID],
          currentYearInclusiveIRAData:
            currentYearAllIRAData &&
            currentYearAllIRAData.currentYearInclusiveRemainingContributions,
          priorYearInclusiveIRAData:
            priorYearAllIRAData &&
            priorYearAllIRAData.priorYearInclusiveRemainingContributions,
          nonRetirementLike,
          disabledStudentId: false,
        };
        Object.assign(fund, cartLookup[fund.agreementID]);

        bankAssociatedWithFund(
          fund,
          selectedBank && selectedBank.bankAgreementID,
          agreementMap
        );

        // Disable fully contributed IRA fund without a note on the fund
        if (fund.fullyContributedIRA && !fund.nonRetirementLike) {
          fund.disabled = true;
        }
        setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );
        updateBuyFundNotesForFund(
          fund,
          personalInformationData,
          IRALimitData,
          selectedBankAgreementMap,
          bucketRoll.nextYearContributionAmountAvailable
        );
        validateBuyFund({
          fund,
          nonRetirementMaxLimit,
          overNonRetirementMaxLimit:
            shoppingCartTotal >
            nonRetirementMaxLimit - buyPendingTransactionsTotal,
          personalInformationData,
        });
        return fund;
      })
      .filter(
        (fund) =>
          ((!customerWebViewEnabled && isSalesforceRep) ||
            !fund.hiddenFromOnlineCustomer) &&
          !fund.viewOnly
      )
);

export const hasRasFundsOnly = createSelector(
  [getAvailableFundList],
  (availableFunds) => {
    if (isEmpty(availableFunds)) {
      return false;
    }
    return (
      availableFunds.filter((availableFund) => {
        return !isRAS(availableFund.productType);
      })?.length === 0
    );
  }
);

export const hasAppFundsOnly = createSelector(
  [getAvailableFundList],
  (availableFunds) => {
    if (isEmpty(availableFunds)) {
      return false;
    }
    return (
      availableFunds.filter((availableFund) => {
        return !isAPP(availableFund.productType);
      })?.length === 0
    );
  }
);

export const getIRAAutoMaxFunds = (state) => {
  return state.iraAutoMaxFunds.funds;
};

export const getAvailableFundListIRAAutoMax = createSelector(
  [
    isSalesforceRep,
    isCustomerWebViewEnabled,
    getAgreementMap,
    getCartLookup,
    getIRALimitData,
    getMoneyMarketReformFundMessages,
    getNewFunds,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getPersonalInformationData,
    getSelectedBank,
    getSelectedBankAgreementMap,
    getUserFundList,
    getAutoMax,
    getHasAutoMaxTransactions,
    getAutoExchanges,
    getRecurringPurchases,
    getRecurringRedemptions,
    getCurrentYearAllIRADataLookUp,
    getPriorYearAllIRADataLookUp,
    getIRAAutoMaxFunds,
  ],
  (
    isSalesforceRep,
    customerWebViewEnabled,
    agreementMap,
    cartLookup,
    IRALimitData,
    moneyMarketReformFundMessages,
    newFunds,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    personalInformationData,
    selectedBank,
    selectedBankAgreementMap,
    userFundList,
    autoMax,
    hasAutoMax,
    autoExchanges,
    recurringPurchases,
    recurringRedemptions,
    currentYearAllIRADataLookUp,
    priorYearAllIRADataLookUp,
    iraAutoMaxFunds
  ) => {
    const getAvailableFundListIRAAutoMaxResult = (
      newFunds.length
        ? [...userFundList, ...newFunds].sort((x, y) => x.sortRank - y.sortRank)
        : userFundList
    )
      .map((origFund) => {
        const currentYearIRADataLookUp =
          currentYearAllIRADataLookUp[origFund.BPID] || {};
        const priorYearIRADataLookUp =
          priorYearAllIRADataLookUp[origFund.BPID] || {};
        const currentYearAllIRAData =
          currentYearIRADataLookUp && currentYearIRADataLookUp[0];
        const priorYearAllIRAData =
          priorYearIRADataLookUp && priorYearIRADataLookUp[0];
        const fund = {
          ...origFund,
          ...currentYearAllIRAData,
          ...priorYearAllIRAData,
          notes: {},
          disabled: false,
          hasRecurring:
            !!autoMax[origFund.agreementID] ||
            !!recurringPurchases[origFund.agreementID] ||
            !!recurringRedemptions[origFund.agreementID] ||
            !!autoExchanges[origFund.agreementID],
          currentYearInclusiveIRAData:
            currentYearAllIRAData &&
            currentYearAllIRAData.currentYearInclusiveRemainingContributions,
          priorYearInclusiveIRAData:
            priorYearAllIRAData &&
            priorYearAllIRAData.priorYearInclusiveRemainingContributions,
          disabledStudentId: false,
        };

        Object.assign(fund, cartLookup[fund.agreementID]);

        bankAssociatedWithFund(
          fund,
          selectedBank && selectedBank.bankAgreementID,
          agreementMap
        );

        // Disable fully contributed IRA fund without a note on the fund
        /*if (!isIRAAutomax() fund.fullyContributedIRA && !fund.nonRetirementLike) {
          fund.disabled = true;
        }*/
        setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );

        updatIRAAutoMaxFundNotesForFund(
          fund,
          personalInformationData,
          IRALimitData,
          selectedBankAgreementMap,
          hasAutoMax
        );

        return fund;
      })
      .filter(
        (fund) =>
          ((!customerWebViewEnabled && isSalesforceRep) ||
            !fund.hiddenFromOnlineCustomer) &&
          !fund.viewOnly
      )
      .filter((f) => iraAutoMaxFunds.includes(f.agreementID));

    return getAvailableFundListIRAAutoMaxResult;
  }
);

export const getEnabledAutoMaxFunds = createSelector(
  [getAvailableFundListIRAAutoMax],
  (availAutoMaxFunds) =>
    availAutoMaxFunds.filter((f) => !f.disabled).map((f) => f.agreementID)
);
export const getIRAAutoMaxContributionRemaining = createSelector(
  getIRALimitData,
  (AutoMax) => {
    const total =
      AutoMax.currentYearRemainingContributions +
      AutoMax.currentYearRemainingAutomaticPurchases;
    return total;
  }
);
/**
 * Exchange fund sorter sorts non-retirement first, retirement second, and then by same sort order that buy uses
 * @param {UserFund} x
 * @param {UserFund} y
 * @returns {number} -1, 0, 1
 */
function recurringFundSorter(x, y) {
  const {
    displayClassification: xDisplayClassification,
    sortRank: xSortRank,
    formattedEntityNames: xNames,
    fundName: xFundName,
  } = x;
  const {
    displayClassification: yDisplayClassification,
    sortRank: ySortRank,
    formattedEntityNames: yNames,
    fundName: yFundName,
  } = y;

  const xType = coalesce(
    PRODUCT_TYPE_SORT_ENUM_MAP[x.productType],
    Number.MAX_VALUE
  );
  const yType = coalesce(
    PRODUCT_TYPE_SORT_ENUM_MAP[y.productType],
    Number.MAX_VALUE
  );
  return xDisplayClassification === yDisplayClassification
    ? xDisplayClassification === 'RETIREMENT'
      ? xSortRank - ySortRank
      : xDisplayClassification === 'NON_RETIREMENT'
      ? xSortRank - ySortRank
      : xNames.localeCompare(yNames)
      ? xNames.localeCompare(yNames)
      : xType - yType
      ? xType - yType
      : xFundName.localeCompare(yFundName)
    : DISPLAY_TYPE_SORT_ENUM_MAP[x.displayClassification] -
        DISPLAY_TYPE_SORT_ENUM_MAP[y.displayClassification];
}

/**
 * Most complex selector to extract and calculate the "available funds" list. This is all funds the user owns,
 * plus all newly added funds, merged with data from both the user fund list and all funds list, with the selected
 * bank flag (bankAssociable & bankAssociated), with fund notes (notes object) and validated against fund rules
 * which adds currentYearError and priorYearError fields for the recurring buy flow.
 * @param state
 * @returns {AvailableFund[]}
 */
export const getAvailableFundListRecurring = createSelector(
  [
    isSalesforceRep,
    isCustomerWebViewEnabled,
    getAgreementMap,
    getBuyPendingTransactionsTotal,
    getCartLookup,
    getIRALimitData,
    getMoneyMarketReformFundMessages,
    getNewFunds,
    getNonRetirementMaxLimit,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getPersonalInformationData,
    getQuickBuyAgreementID,
    getSelectedBank,
    getSelectedBankAgreementMap,
    getShoppingCartTotalForSelectedIRA,
    getUserFundList,
    getAutoMax,
    getHasAutoMaxTransactions,
    getAutoExchanges,
    getRecurringPurchases,
    getRecurringRedemptions,
    getCurrentYearAllIRADataLookUp,
    getPriorYearAllIRADataLookUp,
  ],
  (
    isSalesforceRep,
    customerWebViewEnabled,
    agreementMap,
    buyPendingTransactionsTotal,
    cartLookup,
    IRALimitData,
    moneyMarketReformFundMessages,
    newFunds,
    nonRetirementMaxLimit,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    personalInformationData,
    quickBuyAgreementID,
    selectedBank,
    selectedBankAgreementMap,
    shoppingCartTotalForSelectedIRA,
    userFundList,
    autoMax,
    hasAutoMax,
    autoExchanges,
    recurringPurchases,
    recurringRedemptions,
    currentYearAllIRADataLookUp,
    priorYearAllIRADataLookUp
  ) =>
    (quickBuyAgreementID
      ? userFundList.filter((fund) => fund.agreementID === quickBuyAgreementID)
      : newFunds.length
      ? userFundList.concat(newFunds).sort((x, y) => x.sortRank - y.sortRank)
      : userFundList
    )
      .map((origFund) => {
        const nonRetirementLike = getIsNonRetirementLikeFund(origFund);
        const currentYearIRADataLookUp =
          currentYearAllIRADataLookUp[origFund.BPID] || {};
        const priorYearIRADataLookUp =
          priorYearAllIRADataLookUp[origFund.BPID] || {};
        const currentYearAllIRAData =
          currentYearIRADataLookUp && currentYearIRADataLookUp[0];
        const priorYearAllIRAData =
          priorYearIRADataLookUp && priorYearIRADataLookUp[0];
        const hasRecurring =
          !!autoMax[origFund.agreementID] ||
          !!recurringPurchases[origFund.agreementID] ||
          !!recurringRedemptions[origFund.agreementID] ||
          !!autoExchanges[origFund.agreementID];
        const recurringTransactionTypes = [];

        if (autoExchanges[origFund.agreementID]) {
          recurringTransactionTypes.push(EXCHANGE_TRANSACTION);
        }

        if (recurringPurchases[origFund.agreementID]) {
          recurringTransactionTypes.push(BUY_TRANSACTION);
        }

        if (recurringRedemptions[origFund.agreementID]) {
          recurringTransactionTypes.push(SELL_TRANSACTION);
        }

        const fund = {
          ...origFund,
          ...currentYearAllIRAData,
          ...priorYearAllIRAData,
          notes: {},
          disabled: false,
          hasIRAAutoMax: !!autoMax[origFund.agreementID],
          hasRecurring,
          currentYearInclusiveIRAData:
            currentYearAllIRAData &&
            currentYearAllIRAData.currentYearInclusiveRemainingContributions,
          priorYearInclusiveIRAData:
            priorYearAllIRAData &&
            priorYearAllIRAData.priorYearInclusiveRemainingContributions,
          recurringTransactionTypes,
          nonRetirementLike,
          disabledStudentId: false,
        };

        Object.assign(fund, cartLookup[fund.agreementID]);

        bankAssociatedWithFund(
          fund,
          selectedBank && selectedBank.bankAgreementID,
          agreementMap
        );

        setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );
        updateRecurringBuyFundNotesForFund(
          fund,
          personalInformationData,
          IRALimitData,
          selectedBankAgreementMap,
          hasAutoMax
        );

        let overNonRetirementMaxLimit =
          fund.currentYearAmount >
          nonRetirementMaxLimit - buyPendingTransactionsTotal;

        if (fund.agreementType === 'COLLEGE_SAVINGS') {
          nonRetirementMaxLimit = dailyMaximums.recurring.college_buy;
          overNonRetirementMaxLimit =
            fund.currentYearAmount >
            dailyMaximums.recurring.college_buy - buyPendingTransactionsTotal;
        }

        validateRecurringBuyFund({
          fund,
          nonRetirementMaxLimit,
          overNonRetirementMaxLimit,
          personalInformationData,
          shoppingCartTotalForSelectedIRA,
        });

        return fund;
      })
      .sort(recurringFundSorter)
      .filter(
        (fund) =>
          ((!customerWebViewEnabled && isSalesforceRep) ||
            !fund.hiddenFromOnlineCustomer) &&
          !fund.viewOnly
      )
);

export const getShowMoreRecurringBuyFunds = (state) =>
  state.automaticInvestments.RecurringSystematicPurchasesShow;

export const getRecurringPurchaseFunds = createSelector(
  getUserFundList,
  getExistingBanks,
  getRecurringPurchases,
  (userFunds, existingBanks, recurringPurchases) => {
    const purchaseFunds = [];
    userFunds
      .filter((fund) => recurringPurchases[fund.agreementID])
      .forEach((fund) => {
        recurringPurchases[fund.agreementID].forEach((recurring) => {
          const {
            amount,
            bankAgreementKey,
            investedEveryMonth,
            transferDays,
            transfers,
            startDate,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingTrade,
            pendingUpdate,
            featureStatus,
            specialPayee,
            disbursementType,
            recordKeeperStatus,
          } = recurring;
          const bank = existingBanks.filter((bank) =>
            banksMatch(bank, bankAgreementKey)
          );
          const transferMonths = transfers
            ? transfers.map((transfer) => transfer.month)
            : [];
          const deferMonths = transfers
            ? transfers
                .filter((transfer) => transfer.status.enumerationID === 30)
                .map((transfer) => transfer.month)
            : [];
          const newFund = {
            ...fund,
            currentYearAmount: amount,
            bank,
            investedEveryMonth,
            transferDays,
            transferMonths,
            deferMonths,
            startDate,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingUpdate,
            pendingTrade,
            featureStatus,
            specialPayee,
            selected: true,
            disbursementType: disbursementType ? disbursementType : {},
            recordKeeperStatus,
          };
          const index = findIndex(purchaseFunds, (fund) => {
            return newFund.encryptedUniqueKey === fund.encryptedUniqueKey;
          });
          if (index >= 0) {
            if (
              purchaseFunds[index].currentYearAmount >=
              newFund.currentYearAmount
            ) {
              purchaseFunds[index].pendingTrade = newFund.pendingTrade;
              purchaseFunds[index].featureStatus = newFund.featureStatus;
              purchaseFunds[index].recordKeeperStatus =
                newFund.recordKeeperStatus;
            } else {
              newFund.pendingTrade = purchaseFunds[index].pendingTrade;
              newFund.featureStatus = purchaseFunds[index].featureStatus;
              newFund.recordKeeperStatus =
                purchaseFunds[index].recordKeeperStatus;
              purchaseFunds[index] = newFund;
            }
          } else {
            purchaseFunds.push(newFund);
          }
        });
      });
    return purchaseFunds.sort(
      (a, b) => b.lastUpdatedTimestamp - a.lastUpdatedTimestamp
    );
  }
);

export const getRecurringBuyFundsLength = createSelector(
  getRecurringPurchaseFunds,
  (purchaseFunds) => purchaseFunds.length
);

export const getAutoRMDFunds = createSelector(
  getUserFundList,
  getExistingBanks,
  getSystematicActivityRMDs,
  getUserBPID,
  (userFunds, existingBanks, autoRMDs, BPID) => {
    return autoRMDs && autoRMDs.length > 0
      ? autoRMDs
          .map((rmd) => {
            const currFund = userFunds.filter((fund) => {
              return rmd.sourceAgreementKey.agreementID === fund.agreementID;
            });
            const {
              amount,
              bankAgreementKey,
              deliveryType,
              investedEveryMonth,
              IRAWithholding,
              transferDays,
              transfers,
              startDate,
              targetAgreementKey,
              sourceAgreementKey,
              electionAgreementUniqueId1,
              electionAgreementUniqueId2,
              encryptedUniqueKey,
              lastUpdatedTimestamp,
              pendingTrade,
              pending,
              featureStatus,
              specialPayee,
              disbursementType,
              recordKeeperStatus,
              unit,
              type,
            } = rmd;
            const bank = existingBanks.filter((bank) =>
              banksMatch(bank, bankAgreementKey)
            );
            const transferMonths = transfers
              ? transfers.map((transfer) => transfer.month)
              : [];
            const deferMonths = transfers
              ? transfers
                  .filter((transfer) => transfer.status.enumerationID === 30)
                  .map((transfer) => transfer.month)
              : [];
            const toFundIndex = findIndex(
              userFunds,
              (fund) => fund.agreementID === targetAgreementKey.agreementID
            );
            const toFund =
              toFundIndex >= 0
                ? userFunds[toFundIndex]
                : { agreementType: 'None' };
            const newFund = {
              ...currFund[0],
              currentYearAmountUnit:
                unit && unit.enumerationName ? unit.enumerationName : 'Dollars',
              currentYearAmount: amount,
              bank,
              investedEveryMonth,
              deliveryType,
              IRAWithholding,
              transferDays,
              transferMonths,
              deferMonths,
              toFund,
              startDate,
              electionAgreementUniqueId1,
              electionAgreementUniqueId2,
              encryptedUniqueKey,
              lastUpdatedTimestamp,
              pendingTrade,
              pending,
              featureStatus,
              specialPayee,
              selected: true,
              disbursementType: disbursementType ? disbursementType : {},
              recordKeeperStatus,
              sourceAgreementKey,
              type,
            };
            return newFund;
          })
          .filter((fund) => {
            const ownerArr = fund.agreementParties
              ? fund.agreementParties.filter(
                  (roles) => roles.roleCode === 'OWNR'
                )
              : [];
            const poaArr = fund.agreementParties
              ? fund.agreementParties.filter(
                  (roles) => roles.roleCode === 'AGNT'
                )
              : [];
            fund.isPoa = poaArr.length > 0;
            return ownerArr.length > 0 && ownerArr[0].businessPartyID === BPID
              ? true
              : poaArr.length > 0
              ? poaArr[0].businessPartyID === BPID
              : false;
          })
          .sort((left, right) =>
            left.agreementParties
              ?.filter((f) => f.businessPartyID === BPID)[0]
              .roleCode.localeCompare(
                right.agreementParties?.filter(
                  (f) => f.businessPartyID === BPID
                )[0].roleCode
              )
          )
          .reverse()
      : [];
  }
);

export const getRMDFundsLength = createSelector(
  getAutoRMDFunds,
  (rmdFunds) => rmdFunds.length
);

export const getShowMoreRecurringRedemptionFunds = (state) =>
  state.automaticInvestments.RecurringSystematicRedemptionsShow;

export const getRecurringRedemptionFunds = createSelector(
  getUserFundList,
  getExistingBanks,
  getRecurringRedemptions,
  getAutoRMDFunds,
  (userFunds, existingBanks, recurringRedemptions, systematicActivityRMDs) => {
    const redemptionFunds = [];
    const filterCurrentRMD = (fund) => {
      const ukeys = systematicActivityRMDs.map((fund) => {
        return fund.encryptedUniqueKey;
      });
      return !ukeys.includes(fund.encryptedUniqueKey);
    };
    userFunds
      .filter((fund) => recurringRedemptions[fund.agreementID])
      .forEach((fund) => {
        recurringRedemptions[fund.agreementID].forEach((recurring) => {
          const {
            amount,
            bankAgreementKey,
            sourceAgreementKey,
            deliveryType,
            fiduciaryRedemptionType,
            investedEveryMonth,
            IRAWithholding,
            transferDays,
            transfers,
            startDate,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingTrade,
            pendingUpdate,
            featureStatus,
            specialPayee,
            disbursementType,
            redemptionType,
            recordKeeperStatus,
            unit,
          } = recurring;
          const bank = existingBanks.filter((bank) =>
            banksMatch(bank, bankAgreementKey)
          );
          const transferMonths = transfers
            ? transfers.map((transfer) => transfer.month)
            : [];
          const deferMonths = transfers
            ? transfers
                .filter((transfer) => transfer.status.enumerationID === 30)
                .map((transfer) => transfer.month)
            : [];
          const newFund = {
            ...fund,
            currentYearAmountUnit:
              unit && unit.enumerationName ? unit.enumerationName : 'Dollars',
            currentYearAmount: amount,
            bank,
            deferMonths,
            deliveryType,
            fiduciaryRedemptionType,
            investedEveryMonth,
            IRAWithholding,
            transferDays,
            transferMonths,
            startDate,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingTrade,
            pendingUpdate,
            featureStatus,
            specialPayee,
            selected: true,
            redemptionType,
            disbursementType: disbursementType ? disbursementType : {},
            recordKeeperStatus,
            sourceAgreementKey,
          };
          const index = findIndex(redemptionFunds, (fund) => {
            return (
              newFund.encryptedUniqueKey === fund.encryptedUniqueKey &&
              newFund.lastUpdatedTimestamp === fund.lastUpdatedTimestamp
            );
          });
          if (index >= 0) {
            redemptionFunds[index].pendingTrade = newFund.pendingTrade;
            redemptionFunds[index].featureStatus = newFund.featureStatus;
            redemptionFunds[index].recordKeeperStatus =
              newFund.recordKeeperStatus;
          } else {
            redemptionFunds.push(newFund);
          }
        });
      });
    return redemptionFunds
      .filter(filterCurrentRMD)
      .sort((a, b) => b.lastUpdatedTimestamp - a.lastUpdatedTimestamp);
  }
);

export const getRecurringRedemptionFundsLength = createSelector(
  getRecurringRedemptionFunds,
  (redemptionFunds) => redemptionFunds.length
);

export const getShowMoreAutoExchangeFunds = (state) =>
  state.automaticInvestments.RecurringSystematicExchangesShow;

export const getAutoMaxFunds = createSelector(
  getUserFundList,
  getExistingBanks,
  getAutoMax,
  getUserBPID,
  (userFunds, existingBanks, autoMax, BPID) => {
    const autoMaxFunds = [];
    userFunds
      .filter((fund) => autoMax[fund.agreementID])
      .forEach((fund) => {
        const {
          bankAgreementKey,
          investedEveryMonth,
          transfers,
          startDate,
          electionAgreementUniqueId1,
          electionAgreementUniqueId2,
          encryptedUniqueKey,
          lastUpdatedTimestamp,
          pendingUpdate,
          featureStatus,
          specialPayee,
          disbursementType,
          recordKeeperStatus,
        } = autoMax[fund.agreementID][0];
        let allMonths = [];
        let allDays = [];
        const pendingTradeArr = [];
        let amountArr = [];
        //Auto-Maxes can be broken up if the amounts are different
        //Goes through every auto-max per agreementID and
        // stores all months
        autoMax[fund.agreementID].forEach((account) => {
          allMonths = allMonths.concat(
            account.transfers?.map((transfer) => {
              return transfer.month;
            })
          );
          // stores all pending trades
          pendingTradeArr.push(account.pendingTrade);
          //stores all days
          allDays = allDays.concat(account.transferDays);
          //stores all amounts
          amountArr.push(account.amount);
        });
        //sees if there is a single pending trade
        const pendingTrade = pendingTradeArr.includes(true);

        //filters out any 0 dollar amounts
        amountArr = amountArr.filter((element) => element !== 0);
        let amount;

        //takes the second non-zero amount
        //unless there's only one
        if (amountArr.length > 1) {
          amount = amountArr[1];
        } else {
          amount = amountArr[0];
        }
        const bank = existingBanks.filter((bank) =>
          banksMatch(bank, bankAgreementKey)
        );

        //filters out repeating days and months
        allMonths = _.uniq(allMonths);
        allDays = _.uniq(allDays);

        //re-assigns to
        const transferMonths = allMonths.length > 0 ? allMonths : [];
        const transferDays = allDays.length > 0 ? allDays : [];
        const deferMonths = transfers
          ? transfers
              .filter((transfer) => transfer.status.enumerationID === 30)
              .map((transfer) => transfer.month)
          : [];

        //OWNR vs POA logic
        const ownerArr = fund.agreementParties
          ? fund.agreementParties.filter((roles) => roles.roleCode === 'OWNR')
          : [];
        const poaArr = fund.agreementParties
          ? fund.agreementParties.filter((roles) => roles.roleCode === 'AGNT')
          : [];

        const isOwner =
          ownerArr.length > 0 && ownerArr[0].businessPartyID === BPID;
        const isPOA =
          poaArr.length > 0 &&
          poaArr.some((role) => role.businessPartyID === BPID);
        const newFund = {
          ...fund,
          isOwner,
          isPOA,
          currentYearAmount: amount,
          bank,
          investedEveryMonth,
          transferDays,
          transferMonths,
          deferMonths,
          startDate,
          amount,
          electionAgreementUniqueId1,
          electionAgreementUniqueId2,
          encryptedUniqueKey,
          lastUpdatedTimestamp,
          pendingUpdate,
          pendingTrade,
          allMonths,
          allDays,
          featureStatus,
          specialPayee,
          selected: true,
          disbursementType: disbursementType ? disbursementType : {},
          recordKeeperStatus,
        };

        //goes through userFunds and autoMax's
        const index = findIndex(autoMaxFunds, (fund) => {
          return newFund.encryptedUniqueKey === fund.encryptedUniqueKey;
        });
        if (index >= 0) {
          if (
            autoMaxFunds[index].currentYearAmount >= newFund.currentYearAmount
          ) {
            autoMaxFunds[index].pendingTrade = newFund.pendingTrade;
            autoMaxFunds[index].featureStatus = newFund.featureStatus;
            autoMaxFunds[index].recordKeeperStatus = newFund.recordKeeperStatus;
          } else {
            newFund.pendingTrade = autoMaxFunds[index].pendingTrade;
            newFund.featureStatus = autoMaxFunds[index].featureStatus;
            newFund.recordKeeperStatus = autoMaxFunds[index].recordKeeperStatus;
            autoMaxFunds[index] = newFund;
          }
        } else {
          autoMaxFunds.push(newFund);
        }
      });
    return (
      autoMaxFunds
        .filter((fund) => {
          const ownerArr = fund.agreementParties
            ? fund.agreementParties.filter((roles) => roles.roleCode === 'OWNR')
            : [];
          const poaArr = fund.agreementParties
            ? fund.agreementParties.filter((roles) => roles.roleCode === 'AGNT')
            : [];

          const isOwner =
            ownerArr.length > 0 && ownerArr[0].businessPartyID === BPID;
          const isPOA =
            poaArr.length > 0 &&
            poaArr.some((role) => role.businessPartyID === BPID);
          return isOwner || isPOA;
        })
        .sort((left, right) =>
          left.agreementParties
            ?.filter((f) => f.businessPartyID === BPID)[0]
            .roleCode.localeCompare(
              right.agreementParties?.filter(
                (f) => f.businessPartyID === BPID
              )[0].roleCode
            )
        )
        .reverse() || []
    );
  }
);

export const getAutoMaxPOAsFunds = createSelector(
  getUserAutoMaxPOAs,
  getUserFundList,
  (autoMaxPOAs, userFunds) => {
    return Object.values(autoMaxPOAs).map((autoMaxBPID) => {
      const temp = Object.keys(autoMaxBPID);
      if (temp.length > 0) {
        let one = userFunds.filter((fund) => {
          return fund.agreementID === temp[0];
        });
        one = one?.[0]?.agreementParties.filter(
          (party) => party.roleCode === 'OWNR'
        );
        return one;
      } else {
        return false;
      }
    });
  }
);
export const getOWNRAutoMaxFunds = createSelector(
  getAutoMaxFunds,
  (automaxFunds) => {
    let monthsArr = [];
    let daysArr = [];
    const pendingArray = [];
    //filters through automax fund to find owned automaxs'
    const ownedFunds = automaxFunds.filter((fund) => fund.isOwner);
    //Go through all owned funds and collect the months and dates
    ownedFunds.forEach((fund) => {
      monthsArr = monthsArr.concat(fund.transferMonths);
      daysArr = daysArr.concat(fund.transferDays);
      pendingArray.push(fund.pendingTrade);
    });

    //filter to only keep unique days and months
    monthsArr = _.uniq(monthsArr);
    daysArr = _.uniq(daysArr);
    monthsArr = monthsArr.sort((a, b) => a - b);
    daysArr = daysArr.sort((a, b) => a - b);
    //if one fund is pending all funds are listed as pending
    const pendingTrade = pendingArray.includes(true);
    //all arrays added to the last element for display purposes on AutoMax.js
    if (
      ownedFunds !== undefined &&
      ownedFunds[ownedFunds.length - 1] !== undefined &&
      ownedFunds[ownedFunds.length - 1].transferMonths !== undefined
    ) {
      ownedFunds[ownedFunds.length - 1].transferMonths = monthsArr;
      ownedFunds[ownedFunds.length - 1].allDays = daysArr;
      ownedFunds[ownedFunds.length - 1].pendingTrade = pendingTrade;
    }
    return { ownedFunds };
  }
);

export const getOwnedIRAAutoMaxFunds = createSelector(
  [getOWNRAutoMaxFunds],
  (ownIRAAutoMaxFunds) => ownIRAAutoMaxFunds?.ownedFunds
);

export const getOwnedEnabledIRAAutoMaxFunds = createSelector(
  [getOwnedIRAAutoMaxFunds, getEnabledAutoMaxFunds],
  (ownedFunds, enabledFunds) =>
    ownedFunds.filter((f) => enabledFunds.includes(f.agreementID))
);

export const userSelectedIRAAutoMaxFundsSameAsExisting = createSelector(
  [getOwnedEnabledIRAAutoMaxFunds, getCurrentShoppingCartAgreementIDs],
  (previousFunds, currentFunds) => {
    const existingFunds = previousFunds.map((fund) => {
      return fund.agreementID;
    });
    return (
      existingFunds.every((agreementID) =>
        currentFunds.includes(agreementID)
      ) && existingFunds.length === currentFunds.length
    );
  }
);

export const getIRAAutoMaxFirstOwnedFund = createSelector(
  [getOwnedIRAAutoMaxFunds],
  (ownedFunds) => ownedFunds?.[0]
);
export const getIRAAutoMaxCurrentBank = createSelector(
  [getIRAAutoMaxFirstOwnedFund],
  (firstFund) => firstFund?.bank?.[0]
);

export const getAutoExchangeFunds = createSelector(
  getUserFundList,
  getExistingBanks,
  getAutoExchanges,
  getAutoRMDFunds,
  (userFunds, existingBanks, autoExchanges, systematicActivityRMDs) => {
    const exchangeFunds = [];
    const filterCurrentRMD = (fund) => {
      const ukeys = systematicActivityRMDs.map((fund) => {
        return fund.encryptedUniqueKey;
      });
      return !ukeys.includes(fund.encryptedUniqueKey);
    };
    userFunds
      .filter((fund) => autoExchanges[fund.agreementID])
      .forEach((fund) => {
        autoExchanges[fund.agreementID].forEach((recurring) => {
          const {
            amount,
            bankAgreementKey,
            deliveryType,
            investedEveryMonth,
            IRAWithholding,
            transferDays,
            transfers,
            startDate,
            targetAgreementKey,
            sourceAgreementKey,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingTrade,
            pendingUpdate,
            featureStatus,
            specialPayee,
            disbursementType,
            recordKeeperStatus,
            unit,
          } = recurring;
          const bank = existingBanks.filter((bank) =>
            banksMatch(bank, bankAgreementKey)
          );
          const transferMonths = transfers
            ? transfers.map((transfer) => transfer.month)
            : [];
          const deferMonths = transfers
            ? transfers
                .filter((transfer) => transfer.status.enumerationID === 30)
                .map((transfer) => transfer.month)
            : [];
          const toFundIndex = findIndex(
            userFunds,
            (fund) => fund.agreementID === targetAgreementKey.agreementID
          );
          const toFund =
            toFundIndex >= 0
              ? userFunds[toFundIndex]
              : { agreementType: 'None' };
          const newFund = {
            ...fund,
            currentYearAmountUnit:
              unit && unit.enumerationName ? unit.enumerationName : 'Dollars',
            currentYearAmount: amount,
            bank,
            investedEveryMonth,
            deliveryType,
            IRAWithholding,
            transferDays,
            transferMonths,
            deferMonths,
            toFund,
            startDate,
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
            encryptedUniqueKey,
            lastUpdatedTimestamp,
            pendingTrade,
            pendingUpdate,
            featureStatus,
            specialPayee,
            selected: true,
            disbursementType: disbursementType ? disbursementType : {},
            recordKeeperStatus,
            sourceAgreementKey,
          };
          const index = findIndex(exchangeFunds, (fund) => {
            return newFund.encryptedUniqueKey === fund.encryptedUniqueKey;
          });
          if (index >= 0) {
            exchangeFunds[index].pendingTrade = newFund.pendingTrade;
            exchangeFunds[index].featureStatus = newFund.featureStatus;
            exchangeFunds[index].recordKeeperStatus =
              newFund.recordKeeperStatus;
          } else {
            exchangeFunds.push(newFund);
          }
        });
      });
    return exchangeFunds
      .filter(filterCurrentRMD)
      .sort((a, b) => b.lastUpdatedTimestamp - a.lastUpdatedTimestamp);
  }
);

export const getAutoExchangeFundsLength = createSelector(
  getAutoExchangeFunds,
  (exchangeFunds) => exchangeFunds.length
);

export const getShowMoreRMDFunds = (state) =>
  state.automaticInvestments.RecurringRMDShow;

export const getAutoRMDImpacts = createSelector(
  getAutoExchangeFunds,
  getRecurringRedemptionFunds,
  getUserBusinessPartyId,
  (autoExchanges, autoSells, BPID) => {
    const autoExchangeImpacts = autoExchanges?.filter((agreements) => {
      if (agreements?.agreementParties) {
        const ownerArr = agreements?.agreementParties.filter(
          (roles) => roles.roleCode === 'OWNR'
        );
        return ownerArr.length > 0
          ? ownerArr[0].businessPartyID === BPID &&
              (agreements?.agreementType === 'TRADITIONAL_IRA' ||
                agreements?.agreementType === 'ROLLOVER_IRA')
          : false;
      }
      return false;
    });
    const autoSellImpacts = autoSells?.filter((agreements) => {
      if (agreements?.agreementParties) {
        const ownerArr = agreements?.agreementParties.filter(
          (roles) => roles.roleCode === 'OWNR'
        );
        return ownerArr.length > 0
          ? ownerArr[0].businessPartyID === BPID &&
              (agreements?.agreementType === 'TRADITIONAL_IRA' ||
                agreements?.agreementType === 'ROLLOVER_IRA')
          : false;
      }
      return false;
    });
    return autoExchangeImpacts.length + autoSellImpacts.length;
  }
);

export const getHasAtleastOneFullyContributedIRA = createSelector(
  getAvailableFundList,
  (availableFunds) => availableFunds.some((fund) => fund.fullyContributedIRA)
);

export const getHasAtleastOneFullyContributedIRARecurring = createSelector(
  getAvailableFundListRecurring,
  (availableFunds) => availableFunds.some((fund) => fund.fullyContributedIRA)
);
/**
 * Filter selector of currently selected funds with all data from available funds list. May include funds that are
 * selected but have no amount entered.
 * @param state
 * @returns {AvailableFund[]}
 */
export const getSelectedFunds = createSelector(
  getAvailableFundList,
  (availableFunds) => availableFunds.filter((fund) => fund.selected)
);

export const getSelectedFundsRecurring = createSelector(
  getAvailableFundListRecurring,
  (availableFunds) => availableFunds.filter((fund) => fund.selected)
);

export const getSelectedIRAAutoMaxFunds = createSelector(
  getAvailableFundListIRAAutoMax,
  (availableFunds) => availableFunds.filter((fund) => fund.selected)
);
/**
 * Determines if any selected funds have any error.
 * @function
 * @param state
 * @returns {boolean}
 */
export const getHasSelectedFundsError = createSelector(
  getSelectedFunds,
  (selectedFunds) =>
    selectedFunds.some(
      ({ combinedError, currentYearError, priorYearError }) =>
        combinedError || currentYearError || priorYearError
    )
);

export const getHasSelectedFundsErrorRecurring = createSelector(
  getSelectedFundsRecurring,
  (selectedFunds) =>
    selectedFunds.some(
      ({
        combinedError,
        currentYearError,
        daySelectionError,
        monthSelectionError,
      }) =>
        combinedError ||
        currentYearError ||
        daySelectionError ||
        monthSelectionError
    )
);

/**
 * Filter selector of selected funds including only those funds that have a prior or current year amount, with all
 * data from the available funds list.
 * @param state
 * @returns {AvailableFund[]}
 */
export const getPurchasingFunds = createSelector(
  getAvailableFundList,
  (availableFunds) =>
    availableFunds.filter(
      (fund) =>
        fund.selected &&
        (fund.currentYearAmount > 0 || fund.priorYearAmount > 0)
    )
);

/**
 * Filter selector of selected funds including only those funds that have a prior or current year amount or have selected a day or month, with all
 * data from the available funds list.
 * @param state
 * @returns {AvailableFund[]}
 */
export const getRecurringPurchasingFunds = createSelector(
  getAvailableFundListRecurring,
  (availableFunds) =>
    availableFunds.filter(
      (fund) =>
        fund.selected &&
        (fund.currentYearAmount > 0 ||
          fund.priorYearAmount > 0 ||
          (fund.transferMonths
            ? fund.transferMonths.length > 0
            : fund.transferMonths) ||
          (fund.transferDays
            ? fund.transferDays.length > 0
            : fund.transferDays))
    )
);

/**
 * Array of individual purchases where every fund being purchased is separated into rows for prior and current year
 * amounts and the new fields amount and contributionYear are added.
 * @param state
 * @returns {FlatFund[]}
 */
export const getFlattenedPurchasingFunds = createSelector(
  getPurchasingFunds,
  getDisplayTradeDate,
  (purchasingFunds, displayTradeDate) => {
    const currentYear = formatUnixDate(displayTradeDate, 'YYYY');
    const priorYear = currentYear - 1;
    const flat = [];
    purchasingFunds.forEach((fund) => {
      if (fund.priorYearAmount) {
        flat.push({
          ...fund,
          amount: fund.priorYearAmount,
          contributionYear: priorYear,
        });
      }
      if (fund.currentYearAmount) {
        flat.push({
          ...fund,
          amount: fund.currentYearAmount,
          contributionYear: currentYear,
        });
      }
    });
    return flat;
  }
);

export const getFlattenedPurchasingFundsRecurring = createSelector(
  getPurchasingFunds,
  getDisplayTradeDate,
  (purchasingFunds, displayTradeDate) => {
    const currentYear = formatUnixDate(displayTradeDate, 'YYYY');
    const priorYear = currentYear - 1;
    const flat = [];
    purchasingFunds.forEach((fund) => {
      if (fund.priorYearAmount) {
        flat.push({
          ...fund,
          amount: fund.priorYearAmount,
          contributionYear: priorYear,
        });
      }
      if (fund.currentYearAmount) {
        flat.push({
          ...fund,
          amount: fund.currentYearAmount,
          contributionYear: currentYear,
        });
      }
    });
    return flat;
  }
);

/**
 * Determines if any of the funds being purchased are newly added this session.
 * @param state
 * @returns {boolean}
 */
export const getPurchasingFundsIncludesNew = createSelector(
  getPurchasingFunds,
  (purchasingFunds) => purchasingFunds.some((fund) => fund.newFund)
);

export const getPurchasingFundsIncludesNewRecurring = createSelector(
  getRecurringPurchasingFunds,
  (purchasingFunds) => purchasingFunds.some((fund) => fund.newFund)
);

/**
 * Determines if a user has a stop transfer on at least one of their funds.
 * @param state
 * @return {boolean}
 */
export const hasStopTransferFunds = createSelector(
  getUserFundList,
  (userFundList) =>
    userFundList.some((fund) =>
      checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID)
    )
);

/**
 * Determines if user will over contribute to IRA for current or future year
 * on a total of selected funds based on the maximum allowed limit.
 *
 * @param state
 * @returns {object}
 */
export const getCurrentOrFutureYearIRAContribution = createSelector(
  [
    getPaperlessSettings,
    getRecurringPurchasingFunds,
    getSelectedBank,
    getShoppingCartTotalForSelectedIRA,
  ],
  (
    paperlessSettings,
    recurringPurchasingFunds,
    selectedBank,
    shoppingCartTotalForSelectedIRA
  ) => {
    const chosenBank = selectedBank || {};
    const { paperless, updatedPaperless } = paperlessSettings;
    const oldPaperless = getPaperless(paperless, ProductTypes.MUTUALFUND);
    const systematicTransactions = generateRecurringPurchase(
      updatedPaperless,
      oldPaperless,
      recurringPurchasingFunds,
      chosenBank
    );
    return {
      systematicTransactions,
      totalContribution: shoppingCartTotalForSelectedIRA,
    };
  }
);

export const getAvailableEditFund = createSelector(
  [
    getBuyPendingTransactionsTotal,
    getCartLookup,
    getMoneyMarketReformFundMessages,
    getNonRetirementMaxLimit,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getPersonalInformationData,
    getShoppingCartTotalForSelectedIRA,
    getCurrentYearAllIRADataLookUp,
    automaticEditActivityID,
    getRecurringPurchaseFunds,
    getRecurringRedemptionFunds,
    getAutoExchangeFunds,
  ],
  (
    buyPendingTransactionsTotal,
    cartLookup,
    moneyMarketReformFundMessages,
    nonRetirementMaxLimit,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    personalInformationData,
    shoppingCartTotalForSelectedIRA,
    currentYearAllIRADataLookUp,
    automaticEditActivityID,
    purchaseFunds
  ) => {
    const index = findIndex(purchaseFunds, (fund) => {
      return automaticEditActivityID === fund.encryptedUniqueKey;
    });
    if (automaticEditActivityID && purchaseFunds.length > 0 && index < 0) {
      window.location.replace(`${config.url}${appendBasePath('/automatic')}`);
    }
    const editFund = index >= 0 ? purchaseFunds[index] : {};
    const nonRetirementLike = getIsNonRetirementLikeFund(editFund);
    const currentYearIRADataLookUp =
      currentYearAllIRADataLookUp[editFund.BPID] || {};
    const currentYearAllIRAData =
      currentYearIRADataLookUp && currentYearIRADataLookUp[0];
    const recurringTransactionTypes = [];
    const transferMonths = editFund.transferMonths
      ? editFund.transferMonths.map((month) => {
          return { label: getMonthName(month).toUpperCase(), value: month };
        })
      : [];
    const transferDays = editFund.transferDays
      ? editFund.transferDays.map((day) => {
          return { label: `${day}`, value: day };
        })
      : [];
    const fund = {
      ...editFund,
      ...currentYearAllIRAData,
      transferMonths,
      transferDays,
      notes: {},
      disabled: false,
      currentYearInclusiveIRAData:
        currentYearAllIRAData?.currentYearInclusiveRemainingContributions,
      recurringTransactionTypes,
      nonRetirementLike,
      disabledStudentId: false,
    };

    Object.assign(fund, cartLookup[fund.agreementID]);

    setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
    setFundPendingTransaction(
      fund,
      pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
    );

    let overNonRetirementMaxLimit =
      fund.currentYearAmount >
      nonRetirementMaxLimit - buyPendingTransactionsTotal;

    if (fund.agreementType === 'COLLEGE_SAVINGS') {
      nonRetirementMaxLimit = dailyMaximums.recurring.college_buy;
      overNonRetirementMaxLimit =
        fund.currentYearAmount >
        dailyMaximums.recurring.college_buy - buyPendingTransactionsTotal;
    }

    validateRecurringBuyFund({
      fund,
      nonRetirementMaxLimit,
      overNonRetirementMaxLimit,
      personalInformationData,
      shoppingCartTotalForSelectedIRA,
    });

    return [fund];
  }
);

export const getFlattenedEditFundRecurring = createSelector(
  getAvailableEditFund,
  getDisplayTradeDate,
  (editFund, displayTradeDate) => {
    const currentYear = formatUnixDate(displayTradeDate, 'YYYY');
    const flat = [];
    editFund.forEach((fund) => {
      if (fund.currentYearAmount) {
        flat.push({
          ...fund,
          amount: fund.currentYearAmount,
          contributionYear: currentYear,
        });
      }
    });
    return flat;
  }
);

/**
 * Determines if user will over contribute to IRA for current or future year
 * on a total of selected funds based on the maximum allowed limit.
 *
 * @param state
 * @returns {object}
 */
export const getCurrentOrFutureYearIRAContributionEdit = createSelector(
  getCartLookup,
  getIRALimitData,
  getPaperlessSettings,
  getAvailableEditFund,
  getSelectedBank,
  getShoppingCartTotalForSelectedIRA,
  (
    _cartLookup,
    _IRALimitData,
    paperlessSettings,
    recurringEditFund,
    selectedBank,
    shoppingCartTotalForSelectedIRA
  ) => {
    return {
      systematicTransactions: {
        bankDto: {
          bankAgreementID: selectedBank ? selectedBank.bankAgreementID : null,
          sameDayBank: selectedBank ? selectedBank.sameDayBank : null,
        },
        currentPaperlessConsent: getPaperless(
          paperlessSettings,
          ProductTypes.MUTUALFUND
        ),
        systematicTransactions: [
          {
            amount: recurringEditFund[0].currentYearAmount,
            agreementId: recurringEditFund[0].agreementID,
            systematicActivitySchedule: {
              investedEveryMonth: recurringEditFund[0].investedEveryMonth,
              transferDays: recurringEditFund[0].transferDays.map(
                (day) => day.value
              ),
              transferMonths: recurringEditFund[0].transferMonths.map(
                (month) => month.value
              ),
            },
            systematicTransactionStatus: 'MODIFY',
            systematicTransactionType: 'PURCHASE',
            electionAgreementUniqueId1:
              recurringEditFund[0].electionAgreementUniqueId1,
            electionAgreementUniqueId2:
              recurringEditFund[0].electionAgreementUniqueId2,
          },
        ],
      },
      totalContribution: shoppingCartTotalForSelectedIRA,
    };
  }
);

export const getOriginalFund = createSelector(
  getRecurringPurchaseFunds,
  automaticEditActivityID,
  (recurringPurchasingFunds, editID) => {
    const index = findIndex(recurringPurchasingFunds, (fund) => {
      return editID === fund.encryptedUniqueKey;
    });
    return index >= 0 ? recurringPurchasingFunds[index] : {};
  }
);

export const userFundsSelectvsInfoCallsIRAAutoMax = createSelector(
  getIRAAutoMaxFunds,
  getAllIRAFunds,
  (autoMaxs, iras) => {
    return iras.some((fund) => {
      return autoMaxs.some((agreementID) => {
        return agreementID === fund.agreementID;
      });
    });
  }
);

export const getIsExistingRMDTrust = createSelector(getAutoRMDFunds, (rmds) => {
  if (rmds?.length > 0) {
    return rmds
      .filter(
        (rmd) =>
          rmd.type && rmd.type.enumerationID && rmd.type.enumerationID === 20
      )
      .some(
        (rmd) =>
          rmd.toFund &&
          rmd.toFund.agreementType &&
          rmd.toFund.agreementType === 'TRUST'
      );
  }
  return false;
});

export const getIsRMDFundNonIRA = createSelector(getAutoRMDFunds, (rmds) => {
  return rmds.some(
    (rmd) =>
      rmd.agreementType !== 'ROLLOVER_IRA' &&
      rmd.agreementType !== 'TRADITIONAL_IRA'
  );
});
